import { useState } from 'react'
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, useSortable, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { supabase } from '../lib/supabase'
import { track } from '../lib/analytics'
import ListingPlaceholder from './ListingPlaceholder'

function SortableTile({ listing, position, onMoveToTop }) {
const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: listing.id })
return (
<div
ref={setNodeRef}
{...attributes}
{...listeners}
style={{
transform: CSS.Transform.toString(transform),
transition,
zIndex: isDragging ? 10 : undefined,
opacity: isDragging ? 0.85 : 1,
boxShadow: isDragging ? 'var(--shadow-lg)' : 'var(--shadow-sm)',
background: 'white',
border: '1px solid var(--border)',
borderRadius: 12,
overflow: 'hidden',
cursor: isDragging ? 'grabbing' : 'grab',
// Lets the page still scroll on phones; dragging starts after a press-and-hold
touchAction: 'manipulation',
userSelect: 'none',
WebkitUserSelect: 'none',
WebkitTouchCallout: 'none',
}}
>
<div style={{ position: 'relative', height: 110, background: 'var(--primary-pale)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36 }}>
{listing.images?.[0]
? <img src={listing.images[0]} alt="" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }} />
: <ListingPlaceholder listing={listing} />}
<span style={{ position: 'absolute', top: 6, left: 6, background: 'var(--navy)', color: 'white', borderRadius: 99, fontSize: 11, fontWeight: 700, padding: '2px 8px' }}>#{position}</span>
</div>
<div style={{ padding: '8px 10px' }}>
<div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{listing.title}</div>
<div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>{[listing.suburb, listing.state].filter(Boolean).join(', ') || '—'}</div>
{position > 1 && (
<button
className="btn btn-ghost btn-sm"
style={{ fontSize: 11, padding: '2px 8px' }}
onPointerDown={e => e.stopPropagation()}
onTouchStart={e => e.stopPropagation()}
onClick={e => { e.stopPropagation(); onMoveToTop(listing.id) }}
>
⬆ Move to top
</button>
)}
</div>
</div>
)
}

// Admin-only: drag the browse grid into order and save it for everyone.
// Saving sends the full order of every active listing; any listing not in
// it (archived, hidden, deleted) has its position cleared by the database.
export default function ArrangeListings({ listings, onClose, onSaved }) {
const [order, setOrder] = useState(listings)
const [saving, setSaving] = useState(false)
const [error, setError] = useState('')
const [dirty, setDirty] = useState(false)

const sensors = useSensors(
useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
)

const handleDragEnd = ({ active, over }) => {
if (!over || active.id === over.id) return
setOrder(prev => arrayMove(prev, prev.findIndex(l => l.id === active.id), prev.findIndex(l => l.id === over.id)))
setDirty(true)
}

const moveToTop = (id) => {
setOrder(prev => [prev.find(l => l.id === id), ...prev.filter(l => l.id !== id)])
setDirty(true)
}

const save = async () => {
setSaving(true)
setError('')
const { error: rpcError } = await supabase.rpc('set_listing_order', { listing_ids: order.map(l => l.id) })
setSaving(false)
if (rpcError) { setError(`Couldn't save the order: ${rpcError.message}`); return }
track('admin_listing_order_saved', { count: order.length })
onSaved()
}

return (
<div>
<div style={{ position: 'sticky', top: 'var(--header-h, 64px)', zIndex: 20, background: 'var(--primary-pale)', border: '1px solid var(--primary)', borderRadius: 12, padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
<div style={{ flex: 1, minWidth: 200, fontSize: 13, color: 'var(--navy)' }}>
<strong>Arranging {order.length} listings</strong> — drag to reorder (on a phone, press and hold a card first). Visitors see this order within each distance group.
</div>
<button className="btn btn-ghost btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
<button className="btn btn-gradient btn-sm" onClick={save} disabled={saving || !dirty}>{saving ? 'Saving…' : 'Save order'}</button>
</div>
{error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}
<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
<SortableContext items={order.map(l => l.id)} strategy={rectSortingStrategy}>
<div className="grid-4">
{order.map((l, i) => <SortableTile key={l.id} listing={l} position={i + 1} onMoveToTop={moveToTop} />)}
</div>
</SortableContext>
</DndContext>
</div>
)
}
